/**
 * Premium Rich Text Editor Component
 * A Medium-quality rich text editor with glass theme styling
 * Features:
 * - Exceptional formatting capabilities
 * - Image support with URL embedding
 * - Smooth, butter-like UX
 * - Glass theme integration
 * - Advanced typography
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Focus from '@tiptap/extension-focus';
import Typography from '@tiptap/extension-typography';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Strike from '@tiptap/extension-strike';
import TextAlign from '@tiptap/extension-text-align';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import CharacterCount from '@tiptap/extension-character-count';
import Youtube from '@tiptap/extension-youtube';
import { useSafeThemeClasses } from '@/shared/theme';
import { prepareHtmlForEditor } from '@/shared/utils/blogContentUtils';
import '../../../styles/rich-text-editor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
}

type ImageAlignment = 'left' | 'center' | 'right';
type MediaWidth = '25' | '50' | '75' | '100';

const mediaWidths: MediaWidth[] = ['25', '50', '75', '100'];

const mediaWidthPixels: Record<MediaWidth, number> = {
  '25': 320,
  '50': 560,
  '75': 800,
  '100': 1120,
};

const AlignableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: 'center',
        parseHTML: (element) => element.getAttribute('data-align') || 'center',
        renderHTML: (attributes) => ({
          'data-align': attributes.align,
        }),
      },
      width: {
        default: '100',
        // Read from an existing inline style first (arbitrary drag-resized
        // widths) and fall back to the legacy data-width preset attribute.
        parseHTML: (element) => {
          const styleWidth = element.style.width;
          if (styleWidth?.endsWith('%')) {
            return styleWidth.slice(0, -1);
          }
          return element.getAttribute('data-width') || '100';
        },
        renderHTML: (attributes) => ({
          'data-width': attributes.width,
          // Inline style makes arbitrary (non-preset) percentages from the
          // drag handle render correctly everywhere the saved HTML shows up
          // (reader, previews) - data-width alone only covers the 25/50/75/100
          // CSS breakpoints.
          style: `width: ${attributes.width}%`,
        }),
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

/** Editor-only NodeView for AlignableImage - adds a drag handle to resize
 * the image by arbitrary widths instead of only the 25/50/75/100 presets. */
const ResizableImageView: React.FC<NodeViewProps> = ({ node, updateAttributes, selected }) => {
  const { src, alt, align, width } = node.attrs as {
    src: string;
    alt?: string;
    align: ImageAlignment;
    width: string;
  };
  const innerRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  const justify = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center';

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const container = innerRef.current?.parentElement;
    if (!container) return;

    const containerWidth = container.getBoundingClientRect().width;
    const startX = e.clientX;
    const startWidthPercent = parseFloat(width) || 100;
    setIsResizing(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaPercent = ((moveEvent.clientX - startX) / containerWidth) * 100;
      const next = Math.max(10, Math.min(100, Math.round(startWidthPercent + deltaPercent)));
      updateAttributes({ width: String(next) });
    };
    const onMouseUp = () => {
      setIsResizing(false);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [width, updateAttributes]);

  return (
    <NodeViewWrapper as="div" style={{ display: 'flex', justifyContent: justify }}>
      <div
        ref={innerRef}
        contentEditable={false}
        style={{ width: `${width}%`, position: 'relative', lineHeight: 0 }}
      >
        <img
          src={src}
          alt={alt || ''}
          className="blog-image max-w-full h-auto my-6 rounded-lg"
          data-align={align}
          data-width={width}
          draggable={false}
          style={{
            width: '100%',
            outline: selected ? '2px solid #3b82f6' : 'none',
            outlineOffset: '2px',
            borderRadius: '0.5rem',
          }}
        />
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          style={{
            position: 'absolute',
            right: -16,
            bottom: -16,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'nwse-resize',
            opacity: selected || isResizing ? 1 : 0,
            pointerEvents: selected || isResizing ? 'auto' : 'none',
            transition: 'opacity 0.15s ease',
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 4,
              background: '#3b82f6',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
    </NodeViewWrapper>
  );
};

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ 
  onClick, 
  isActive, 
  disabled, 
  children, 
  title,
}) => {
  const theme = useSafeThemeClasses();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rich-text-editor-toolbar-button rounded transition-colors duration-150 ${isActive 
          ? `${theme.buttonPrimary} ${theme.textPrimary}` 
          : `${theme.buttonGhost} ${theme.textSecondary} hover:${theme.textPrimary}`
        } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
};

const ImageUploadModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, alt?: string) => void;
  theme: ReturnType<typeof useSafeThemeClasses>;
}> = ({ isOpen, onClose, onInsert, theme }) => {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInsert = async () => {
    if (!url.trim()) return;
    
    setIsLoading(true);
    // Validate image URL by trying to load it
    const img = new window.Image();
    img.onload = () => {
      onInsert(url.trim(), alt.trim() || 'Image');
      setUrl('');
      setAlt('');
      setIsLoading(false);
      onClose();
    };
    img.onerror = () => {
      setIsLoading(false);
      toast.error('Invalid image URL. Please check the URL and try again.');
    };
    img.src = url.trim();
  };

  if (!isOpen) return null;

  // Portaled to <body>: position:fixed breaks out of the viewport when
  // mounted inside a backdrop-blurred page wrapper (see Modal.tsx for the
  // full explanation - same containing-block issue applies here).
  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className={`${theme.card} rounded-xl p-6 max-w-md w-full shadow-2xl`}>
        <h3 className={`text-lg font-semibold mb-4 ${theme.textHeading}`}>Insert Image</h3>
        
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
              Image URL *
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className={`w-full px-3 py-2 ${theme.input} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors`}
              autoFocus
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme.textPrimary}`}>
              Alt Text (optional)
            </label>
            <input
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Describe the image..."
              className={`w-full px-3 py-2 ${theme.input} rounded-lg ${theme.textPrimary} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors`}
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2 ${theme.buttonSecondary} ${theme.textPrimary} rounded-lg transition-colors`}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            disabled={!url.trim() || isLoading}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              !url.trim() || isLoading
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
            }`}
          >
            {isLoading ? 'Validating...' : 'Insert Image'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = "Tell your story...",
  className = '',
  readOnly = false,
}) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showAdvancedTools, setShowAdvancedTools] = useState(false);
  const theme = useSafeThemeClasses();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: false,
        orderedList: false,
        listItem: false,
        strike: false,
        hardBreak: {
          keepMarks: true,
        },
        paragraph: {
          HTMLAttributes: {
            class: 'editor-paragraph',
          },
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Focus.configure({
        className: 'has-focus',
      }),
      Typography,
      Dropcursor.configure({
        color: '#3b82f6',
        width: 2,
      }),
      Gapcursor,
      // Text formatting extensions
      Underline,
      Strike,
      Subscript,
      Superscript,
      // List extensions
      BulletList.configure({
        HTMLAttributes: {
          class: 'my-4',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'my-4',
        },
      }),
      ListItem,
      // Text alignment
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      AlignableImage.configure({
        HTMLAttributes: {
          class: 'blog-image max-w-full h-auto my-6 rounded-lg',
        },
        allowBase64: true,
      }),
      Youtube.configure({
        nocookie: true,
        modestBranding: true,
        HTMLAttributes: {
          class: 'blog-video',
        },
      }),
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
        HTMLAttributes: {
          class: 'blog-link decorated-link',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      TextStyle,
      Color,
      Highlight.configure({
        HTMLAttributes: {
          class: 'bg-yellow-200/40 dark:bg-yellow-500/20 px-1 rounded',
        },
      }),
      CharacterCount,
    ],
    content: prepareHtmlForEditor(content),
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-lg dark:prose-invert max-w-none focus:outline-none prose-headings:font-bold prose-headings:tracking-tight prose-h1:text-2xl sm:prose-h1:text-3xl lg:prose-h1:text-4xl prose-h1:leading-tight prose-h2:text-xl sm:prose-h2:text-2xl lg:prose-h2:text-3xl prose-h2:leading-tight prose-h3:text-lg sm:prose-h3:text-xl lg:prose-h3:text-2xl prose-h3:leading-tight prose-p:text-base sm:prose-p:text-lg prose-p:leading-relaxed prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50/50 dark:prose-blockquote:bg-blue-900/20 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-img:rounded-lg prose-img:shadow-lg ${readOnly ? 'cursor-default' : 'min-h-[60vh] cursor-text'}`,
      },
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    // Avoid resetting while typing (prevents cursor jump and whitespace loss)
    if (editor.isFocused) return;
    const prepared = prepareHtmlForEditor(content);
    if (prepared !== editor.getHTML()) {
      editor.commands.setContent(prepared, false, { preserveWhitespace: 'full' });
    }
  }, [content, editor]);

  const addImage = useCallback((url: string, alt: string = 'Image') => {
    if (editor) {
      editor.chain().focus().setImage({ src: url, alt }).updateAttributes('image', { width: '100' }).run();
    }
  }, [editor]);

  const addVideo = useCallback(() => {
    const url = window.prompt('YouTube video URL:');
    if (!url || !editor) return;

    const width = mediaWidthPixels['100'];
    editor.chain().focus().setYoutubeVideo({
      src: url,
      width,
      height: Math.round(width * 9 / 16),
    }).run();
  }, [editor]);

  const addLink = useCallback(() => {
    const url = window.prompt('Link URL:');
    if (url) {
      editor?.chain().focus().setLink({ href: url }).run();
    }
  }, [editor]);

  const alignSelection = useCallback((align: ImageAlignment) => {
    if (!editor) return;

    if (editor.isActive('image')) {
      editor.chain().focus().updateAttributes('image', { align }).run();
      return;
    }

    editor.chain().focus().setTextAlign(align).run();
  }, [editor]);

  const isAlignmentActive = useCallback((align: ImageAlignment) => {
    if (!editor) return false;
    return editor.isActive('image', { align }) || editor.isActive({ textAlign: align });
  }, [editor]);

  const isMediaActive = !!editor && (editor.isActive('image') || editor.isActive('youtube'));

  const getActiveMediaWidth = (): MediaWidth => {
    if (!editor) return '100';

    if (editor.isActive('image')) {
      const width = String(editor.getAttributes('image').width || '100');
      return mediaWidths.includes(width as MediaWidth) ? width as MediaWidth : '100';
    }

    if (editor.isActive('youtube')) {
      const width = Number(editor.getAttributes('youtube').width);
      if (width <= mediaWidthPixels['25']) return '25';
      if (width <= mediaWidthPixels['50']) return '50';
      if (width <= mediaWidthPixels['75']) return '75';
    }

    return '100';
  };

  const resizeSelectedMedia = (width: MediaWidth) => {
    if (!editor) return;

    if (editor.isActive('image')) {
      editor.chain().focus().updateAttributes('image', { width }).run();
      return;
    }

    if (editor.isActive('youtube')) {
      const pixelWidth = mediaWidthPixels[width];
      editor.chain().focus().updateAttributes('youtube', {
        width: pixelWidth,
        height: Math.round(pixelWidth * 9 / 16),
      }).run();
    }
  };

  if (!editor) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className={`blog-html-content rich-editor-content max-w-none ${className}`}>
        <EditorContent editor={editor} />
      </div>
    );
  }

  const toolbar = (
    <div
      data-toolbar
      className="rich-text-editor-toolbar px-3 py-2"
    >
            {/* Default Essential Tools */}
            <div className="rich-text-editor-toolbar-group">
              {/* Bold, Italic, Underline */}
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                title="Bold (Ctrl+B)"
              >
                <span className="font-bold text-sm">B</span>
              </ToolbarButton>
              
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                title="Italic (Ctrl+I)"
              >
                <span className="italic text-sm">I</span>
              </ToolbarButton>
              
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                isActive={editor.isActive('underline')}
                title="Underline (Ctrl+U)"
              >
                <span className="underline text-sm">U</span>
              </ToolbarButton>
              
              <div className="rich-text-editor-toolbar-divider"></div>
              
              {/* Lists */}
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive('bulletList')}
                title="Bullet List"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                  <circle cx="2" cy="3" r="1.5" />
                  <path d="M5 3h9v1H5z" />
                  <circle cx="2" cy="8" r="1.5" />
                  <path d="M5 8h9v1H5z" />
                  <circle cx="2" cy="13" r="1.5" />
                  <path d="M5 13h9v1H5z" />
                </svg>
              </ToolbarButton>
              
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive('orderedList')}
                title="Numbered List"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                  <text x="1" y="4" fontSize="3" fontWeight="bold" fill="currentColor">1</text>
                  <path d="M4.5 3h9.5v1H4.5z" />
                  <text x="1" y="9" fontSize="3" fontWeight="bold" fill="currentColor">2</text>
                  <path d="M4.5 8h9.5v1H4.5z" />
                  <text x="1" y="14" fontSize="3" fontWeight="bold" fill="currentColor">3</text>
                  <path d="M4.5 13h9.5v1H4.5z" />
                </svg>
              </ToolbarButton>
              
              <div className="rich-text-editor-toolbar-divider"></div>
              
              {/* Link and Image */}
              <ToolbarButton
                onClick={addLink}
                isActive={editor.isActive('link')}
                title="Add Link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </ToolbarButton>
              
              <ToolbarButton
                onClick={() => setIsImageModalOpen(true)}
                title="Insert Image"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </ToolbarButton>

              <ToolbarButton
                onClick={addVideo}
                isActive={editor.isActive('youtube')}
                title="Insert YouTube Video"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </ToolbarButton>
              
              <div className="rich-text-editor-toolbar-divider"></div>
              
              {/* Heading Selector */}
              <select
                onChange={(e) => {
                  const level = e.target.value;
                  if (level === 'paragraph') {
                    editor.chain().focus().setParagraph().run();
                  } else {
                    editor.chain().focus().toggleHeading({ level: parseInt(level) as 1 | 2 | 3 }).run();
                  }
                }}
                value={
                  editor.isActive('heading', { level: 1 }) ? '1' :
                  editor.isActive('heading', { level: 2 }) ? '2' :
                  editor.isActive('heading', { level: 3 }) ? '3' :
                  'paragraph'
                }
                className={`rich-text-editor-toolbar-select text-xs rounded ${theme.input} ${theme.textPrimary}`}
                title="Text Style"
              >
                <option value="paragraph">Paragraph</option>
                <option value="1">Heading 1</option>
                <option value="2">Heading 2</option>
                <option value="3">Heading 3</option>
              </select>
              
              <div className="rich-text-editor-toolbar-divider"></div>

              {/* Text and Image Alignment */}
              <ToolbarButton
                onClick={() => alignSelection('left')}
                isActive={isAlignmentActive('left')}
                title="Align Left"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M3 8h12m-12 4h18M3 16h12" />
                </svg>
              </ToolbarButton>

              <ToolbarButton
                onClick={() => alignSelection('center')}
                isActive={isAlignmentActive('center')}
                title="Align Center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M7 8h10M3 12h18M7 16h10" />
                </svg>
              </ToolbarButton>

              <ToolbarButton
                onClick={() => alignSelection('right')}
                isActive={isAlignmentActive('right')}
                title="Align Right"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M9 8h12M3 12h18M9 16h12" />
                </svg>
              </ToolbarButton>
              
              {isMediaActive && (
                <>
                  <div className="rich-text-editor-toolbar-divider"></div>
                  <select
                    value={getActiveMediaWidth()}
                    onChange={(e) => resizeSelectedMedia(e.target.value as MediaWidth)}
                    className={`rich-text-editor-toolbar-select text-xs rounded ${theme.input} ${theme.textPrimary}`}
                    title="Media Size"
                  >
                    <option value="25">25%</option>
                    <option value="50">50%</option>
                    <option value="75">75%</option>
                    <option value="100">100%</option>
                  </select>
                </>
              )}
              
              <div className="rich-text-editor-toolbar-divider"></div>
              
              {/* More Options Toggle */}
              <ToolbarButton
                onClick={() => setShowAdvancedTools(!showAdvancedTools)}
                isActive={showAdvancedTools}
                title="More Options"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </ToolbarButton>
            </div>
            
            {/* Advanced Tools - Hidden by default */}
            {showAdvancedTools && (
              <div className="rich-text-editor-toolbar-group rich-text-editor-toolbar-advanced">
                {/* Strikethrough */}
                <ToolbarButton
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  isActive={editor.isActive('strike')}
                  title="Strikethrough"
                >
                  <span className="line-through text-sm">S</span>
                </ToolbarButton>
                
                {/* Subscript/Superscript */}
                <ToolbarButton
                  onClick={() => editor.chain().focus().toggleSubscript().run()}
                  isActive={editor.isActive('subscript')}
                  title="Subscript"
                >
                  <span className="text-xs">X₂</span>
                </ToolbarButton>
                
                <ToolbarButton
                  onClick={() => editor.chain().focus().toggleSuperscript().run()}
                  isActive={editor.isActive('superscript')}
                  title="Superscript"
                >
                  <span className="text-xs">X²</span>
                </ToolbarButton>
                
              </div>
            )}
    </div>
  );

  return (
    <div className={`rich-text-editor-root flex flex-col ${className}`}>
      {/* Sticky toolbar — stays below page header (h-16) while scrolling */}
      <div
        className={`sticky top-16 z-30 shrink-0 ${theme.card} rounded-t-lg rounded-b-none`}
      >
        {toolbar}
      </div>

      <div
        className={`rich-text-editor-body relative flex-1 min-h-[60vh] ${theme.card} border-t-0 rounded-t-none rounded-b-lg`}
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent
          editor={editor}
          className={`px-6 py-8 sm:px-10 sm:py-10 focus-within:outline-none max-w-none rich-editor-content ${theme.textPrimary}`}
        />

        {editor && (
          <div className={`mt-4 text-xs whitespace-nowrap ${theme.textMuted} ${theme.card} px-3 py-1 rounded-full shadow-sm`}>
            {editor.storage.characterCount.words()} words
          </div>
        )}
      </div>

      <ImageUploadModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        onInsert={addImage}
        theme={theme}
      />
    </div>
  );
};

export default RichTextEditor;
